// TDU stage-map — route file parsing (KML/GPX → classified route).
//
// Uses @tmcw/togeojson. In the browser the native DOMParser is used; in node tests inject one
// (e.g. @xmldom/xmldom) via opts.DOMParserImpl. togeojson does NOT unzip .kmz (plan §10).

import { kml as kmlToGeo, gpx as gpxToGeo } from '@tmcw/togeojson';
import { classifyMarker, applyOverride, matchActiveLaps, parseLapsDirective, stripLapsDirective } from './classify.js';

/** @returns {GeoJSON.FeatureCollection} */
export function parseToGeoJSON(text, { format = 'auto', DOMParserImpl } = {}) {
    const DP = DOMParserImpl || (typeof DOMParser !== 'undefined' ? DOMParser : null);
    if (!DP) throw new Error('No DOMParser available; pass opts.DOMParserImpl when running outside a browser.');
    const doc = new DP().parseFromString(text, 'text/xml');
    const fmt = format === 'auto' ? (/<gpx[\s>]/i.test(text) ? 'gpx' : 'kml') : format;
    return fmt === 'gpx' ? gpxToGeo(doc) : kmlToGeo(doc);
}

/**
 * Extract the route line + classified markers from a parsed FeatureCollection.
 * The route is the longest LineString; every Point becomes a classified marker.
 * @param {GeoJSON.FeatureCollection} geojson
 * @param {{ overrides?: Record<number, object>, pointLaps?: Array<{match:string, laps:string}> }} [opts]
 * @returns {{ line: number[][], markers: Array<object>, raceFeatures: Array<object> }}
 */
export function extractRoute(geojson, { overrides = {}, pointLaps = [] } = {}) {
    const features = geojson.features || [];
    let line = [];
    const markers = [];
    let idx = 0;

    for (const f of features) {
        const g = f.geometry;
        if (!g) continue;
        if (g.type === 'LineString') {
            if (g.coordinates.length > line.length) line = g.coordinates;
        } else if (g.type === 'MultiLineString') {
            const flat = g.coordinates.flat();
            if (flat.length > line.length) line = flat;
        } else if (g.type === 'Point') {
            const name = (f.properties && f.properties.name) || '';
            // togeojson exposes the KML IconStyle href as properties.icon — these are the branded
            // race icons (Start Flag, KOM, Feed Zone, Lookout…) the old Google map rendered.
            const iconUrl = (f.properties && f.properties.icon) || null;
            // KML <description> — vantage points carry real detail here (e.g. "Mengler's Hill
            // Lookout … the race goes past x3"). Often plain text, not CDATA.
            const description = (f.properties && f.properties.description) || '';
            const marker = {
                index: idx,
                lng: g.coordinates[0],
                lat: g.coordinates[1],
                iconUrl,
                // The "Laps:" directive is machine config, not popup text — strip it from what's shown.
                description: stripLapsDirective(description),
                ...applyOverride(classifyMarker(name), overrides[idx]),
            };
            // Lap-selection precedence: an explicit per-marker index override (set above) wins; then a
            // "Laps: 1,2" directive in the KML description (the self-describing, by-the-source way);
            // then the back-office "Point laps" name rules. So a stage author can pin which laps a
            // repeated circuit point (sprint, end-of-neutral) is contested on in the KML itself.
            if (marker.isRaceFeature && !marker.activeLaps) {
                const laps = parseLapsDirective(description) || matchActiveLaps(marker.label, pointLaps);
                if (laps) marker.activeLaps = laps;
            }
            markers.push(marker);
            idx++;
        }
    }

    const raceFeatures = markers.filter((m) => m.isRaceFeature && !m.hidden);
    return { line, markers, raceFeatures };
}
